// src/services/englishPracticeClientService.js
import { fetchFromBackend, fetchWithFormData } from "../services/apiClient"; // Adjust path as needed

const SERVICE_PATH = "api/english-practice";

/**
 * Helper function to handle common error logging and re-throwing.
 * Logs the full error in non-production environments and throws a user-friendly error.
 * @param {string} functionName - The name of the function where the error occurred.
 * @param {Error} error - The original error object.
 * @param {string} userFriendlyMessage - The message to show to the user.
 */
const handleError = (functionName, error, userFriendlyMessage) => {
  // Log the full error in development or test environments for debugging.
  // In production, we avoid logging sensitive or internal error details to the console.
  if (process.env.NODE_ENV !== "production") {
    console.error(`[${functionName}] Detailed error:`, error);
  }
  // Throw a new error with a user-friendly message, hiding internal details.
  throw new Error(userFriendlyMessage);
};

/**
 * Retrieves a proficiency assessment task from the backend.
 * @param {number} [redoSessionId=null] - Optional session ID to redo the assessment task from.
 * @returns {Promise<object>} - The proficiency assessment task object.
 *   Example: { taskType: "read_aloud", title: "...", instructions: "...", content: { textToRead: "..." }, evaluationCriteria: "..." }
 */
export const getProficiencyAssessmentTask = async (redoSessionId = null) => {
  try {
    const queryParams = redoSessionId ? `?redoSessionId=${redoSessionId}` : "";
    return await fetchFromBackend(
      `${SERVICE_PATH}/proficiency-task${queryParams}`,
      {},
      "GET"
    );
  } catch (error) {
    handleError(
      "getProficiencyAssessmentTask",
      error,
      "Failed to get proficiency assessment task. Please try again later."
    );
  }
};

/**
 * Submits a proficiency assessment for evaluation.
 * @param {object} assessmentTask - The task object that was presented to the user.
 * @param {Blob} audioBlob - The audio recording of the user's spoken response.
 * @returns {Promise<object>} - The evaluation result.
 *   Example: { estimatedProficiencyLevel: "Intermediate", strengths: [], areasForImprovement: [], detailedFeedback: "..." }
 */
export const evaluateProficiencyAssessment = async (
  assessmentTaskId,
  audioBlob
) => {
  try {
    const formData = new FormData();
    // Provide a default filename if blob has no name (e.g., from MediaRecorder)
    formData.append("audioFile", audioBlob, audioBlob.name || "audio.webm");
    formData.append("assessmentTaskId", assessmentTaskId);
    return await fetchWithFormData(
      `${SERVICE_PATH}/evaluate-proficiency`,
      formData
    );
  } catch (error) {
    handleError(
      "evaluateProficiencyAssessment",
      error,
      "Failed to evaluate proficiency. Please try again later."
    );
  }
};

/**
 * Generates a personalized learning plan based on proficiency results.
 * @param {int} SessionId - The proficiency evaluation object.
 * @param {string} [learningGoals=null] - Optional user-defined learning goals.
 * @param {number} [numberOfSessions=5] - Desired number of sessions in the plan.
 * @returns {Promise<object>} - The personalized learning plan.
 *   Example: { planTitle: "...", overallGoal: "...", sessions: [{ sessionId: "...", title: "...", ... }] }
 */
export const generatePersonalizedLearningPlan = async (
  SessionId,
  learningGoals = null,
  numberOfSessions = 10
) => {
  try {
    return await fetchFromBackend(`${SERVICE_PATH}/generate-learning-plan`, {
      body: { SessionId, learningGoals, numberOfSessions },
    });
  } catch (error) {
    handleError(
      "generatePersonalizedLearningPlan",
      error,
      "Failed to generate learning plan. Please try again later."
    );
  }
};

/**
 * Gets speech analysis feedback for a specific task in a learning plan.
 * @param {object} planTask - The specific learning plan task object.
 * @param {string} userCleanTranscription - The cleaned transcription of the user's response.
 * @param {string} userAssessedLevel - The user's initially assessed proficiency level (e.g., "Intermediate").
 * @param {number} [audioDurationSeconds=null] - Optional duration of the audio.
 * @param {Blob} audioBlob - The audio recording of the user's spoken response.
 * @returns {Promise<object>} - The detailed speech feedback object.
 *   Example: { overallClarityScore: 8, speakingRate: { assessment: "Appropriate" }, pronunciationIssues: [], ... }
 */
export const getSpeechAnalysisForPlanTask = async (
  sessionItemId,
  userCleanTranscription,
  userAssessedLevel,
  audioDurationSeconds = null,
  audioBlob
) => {
  try {
    const formData = new FormData();
    // Provide a default filename if blob has no name (e.g., from MediaRecorder)
    formData.append("audioFile", audioBlob, audioBlob.name || "audio.webm");
    formData.append(
      "speechAnalysisRequest",
      JSON.stringify({
        sessionItemId,
        userCleanTranscription,
        userAssessedLevel,
        audioDurationSeconds,
      })
    );
    return await fetchWithFormData(`${SERVICE_PATH}/speech-analysis`, formData);
  } catch (error) {
    handleError(
      "getSpeechAnalysisForPlanTask",
      error,
      "Failed to get speech analysis. Please try again later."
    );
  }
};
export const GetEnglishPracticeSessionsSummary = async (page = 1, pageSize = 10) => {
  try {
    return await fetchFromBackend(
      `${SERVICE_PATH}/english-practice-sessions-summary?page=${page}&pageSize=${pageSize}`,
      {},
      "GET"
    );
  } catch (error) {
    handleError(
      "GetEnglishPracticeSessionsSummary",
      error,
      "Failed to get English practice sessions summary. Please try again later."
    );
  }
};

// Mock implementation for GetEnglishPracticeSessionDetails
export const GetEnglishPracticeSessionDetails = async (sessionId) => {
  try {
    return await fetchFromBackend(
      `${SERVICE_PATH}/sessions/${sessionId}`,
      {},
      "GET"
    );
  } catch (error) {
    handleError(
      "GetEnglishPracticeSessionDetails",
      error,
      "Failed to get English practice session details. Please try again later."
    );
  }
};
